import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const moveCircle1 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(30px, -40px);
  }
  50% {
    transform: translate(-20px, -30px);
  }
  75% {
    transform: translate(40px, 20px);
  }
`;

const moveCircle2 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(-40px, 20px);
  }
  50% {
    transform: translate(30px, 40px);
  }
  75% {
    transform: translate(-30px, -40px);
  }
`;

const moveCircle3 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(20px, 50px);
  }
  50% {
    transform: translate(-40px, 30px);
  }
  75% {
    transform: translate(40px, -20px);
  }
`;

const serviceOfferings = [
  {
    icon: "🖥️",
    title: "Infrastructure Management",
    desc: "24/7 monitoring and proactive management of servers, networks, storage, and data centers."
  },
  {
    icon: "🛡️",
    title: "Security & Compliance",
    desc: "Advanced threat detection, vulnerability management, and regulatory compliance monitoring."
  },
  {
    icon: "☁️",
    title: "Cloud Services",
    desc: "Cloud infrastructure setup, migration, optimization, and ongoing multi-cloud management."
  },
  {
    icon: "🔧",
    title: "Help Desk & Support",
    desc: "Comprehensive technical support with rapid response times and resolution expertise."
  },
  {
    icon: "⚡",
    title: "Disaster Recovery",
    desc: "Backup solutions, recovery planning, and business continuity assurance with SLAs."
  },
  {
    icon: "📊",
    title: "Performance Optimization",
    desc: "System tuning, capacity planning, and continuous improvement of IT infrastructure."
  }
];

const whyChooseUs = [
  {
    icon: "🎯",
    title: "Proactive Monitoring",
    desc: "24/7 monitoring detects and resolves issues before they impact your business operations."
  },
  {
    icon: "⚙️",
    title: "Expert Team",
    desc: "Certified IT professionals with deep expertise across technologies and industry standards."
  },
  {
    icon: "💰",
    title: "Cost Efficiency",
    desc: "Predictable costs through managed services eliminate unexpected IT expenses and downtime."
  },
  {
    icon: "🚀",
    title: "Scalability",
    desc: "Grow your infrastructure without disruption, scaling resources as your business evolves."
  }
];

const mitExpertise = [
  {
    title: "Network Management",
    desc: "Design, deployment, and optimization of secure, scalable network infrastructure."
  },
  {
    title: "Server Administration",
    desc: "On-premises and cloud server management with high availability and redundancy."
  },
  {
    title: "Backup & Disaster Recovery",
    desc: "Comprehensive backup strategies and recovery solutions to ensure business continuity."
  },
  {
    title: "Cybersecurity",
    desc: "Threat monitoring, incident response, and security posture management 24/7."
  },
  {
    title: "Cloud Management",
    desc: "Multi-cloud administration, cost optimization, and seamless hybrid environments."
  }
];

const ManagedITServices = () => {
  useDocumentTitle("Briozon - Managed IT Services");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.15) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle1} 15s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, rgba(118, 75, 162, 0) 70%)",
            animation: `${moveCircle2} 18s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.1) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle3} 20s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />

        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  MANAGED IT SERVICES
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Reliable IT{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Infrastructure
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Focus on your business while we manage your IT infrastructure. Our 24/7 managed IT services ensure your systems run smoothly, securely, and cost-effectively. From proactive monitoring to disaster recovery, we handle all aspects of your IT environment.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  🖥️
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🛡️</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    24/7 Security
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>☁️</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Cloud Ready
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>📊</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Monitoring
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>⚡</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    24/7 Support
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 2 — Overview: text + services checklist */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
                MANAGED IT SERVICES
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 3, lineHeight: 1.2 }}>
                Comprehensive Managed IT{" "}
                <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  Solutions
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, mb: 3, textAlign: "justify" }}>
                Our managed IT services provide complete oversight and support of your entire IT infrastructure. We proactively monitor systems 24/7, anticipate issues before they impact your business, and ensure optimal performance, security, and uptime. Let us handle the complexity while you focus on growth.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, textAlign: "justify" }}>
                From infrastructure monitoring and patch management to security operations and cloud management, we deliver end-to-end IT services with predictable costs and guaranteed SLAs. Our team of certified professionals brings expertise across all platforms and technologies to support your mission-critical operations.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)", borderRadius: "20px", p: { xs: 3, md: 4 }, border: "1px solid rgba(91,124,250,0.12)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase" }}>
                  Core Services
                </Typography>
                {["Infrastructure Management", "24/7 Monitoring", "Cloud Services", "Security & Compliance", "Disaster Recovery", "Help Desk Support"].map((item) => (
                  <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ color: "#fff", fontSize: "12px", fontWeight: 700 }}>✓</Typography>
                    </Box>
                    <Typography sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", fontWeight: 500 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 3 — Capabilities: 3 tiles + 2-col block */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fafbff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              EXPERTISE
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Our Managed IT{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Expertise
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, maxWidth: "700px", textAlign: "justify" }}>
              Briozon specializes in managed IT services that transform your infrastructure into a competitive advantage. We combine proven methodologies, industry best practices, and cutting-edge technologies to deliver reliable, secure, and scalable IT operations.
            </Typography>
          </Box>

          <Box sx={{ mb: { xs: 6, md: 8 } }}>
            <Grid container spacing={3}>
              {[
                { icon: "🛡️", title: "Proactive Security", desc: "Continuous threat monitoring, vulnerability scanning, and incident response to keep your systems protected around the clock." },
                { icon: "☁️", title: "Cloud Management", desc: "Multi-cloud administration, cost optimization, and seamless hybrid environment management for modern workloads." },
                { icon: "📊", title: "24/7 Monitoring", desc: "Real-time visibility into system health, performance metrics, and availability across your entire IT estate." },
              ].map((item) => (
                <Grid item xs={12} sm={4} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "16px", background: "#fff", border: "1px solid rgba(91,124,250,0.1)", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { transform: "translateY(-6px)", boxShadow: "0 16px 40px rgba(91,124,250,0.12)", borderColor: "rgba(91,124,250,0.3)" } }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: "14px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem", mb: 2.5, boxShadow: "0 6px 16px rgba(91,124,250,0.25)" }}>
                      {item.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 1, fontSize: { xs: "18px", sm: "20px" } }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Box sx={{ background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)", borderRadius: "20px", p: { xs: 3, md: 4 }, border: "1px solid rgba(91,124,250,0.12)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase" }}>
                  Key Strengths
                </Typography>
                {[
                  { icon: "⚡", label: "Guaranteed SLAs & Uptime" },
                  { icon: "💰", label: "Predictable Monthly Costs" },
                  { icon: "🔧", label: "Rapid Issue Resolution" },
                ].map((f) => (
                  <Box key={f.label} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", flexShrink: 0 }}>
                      {f.icon}
                    </Box>
                    <Typography sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", fontWeight: 600 }}>{f.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "22px", sm: "28px", md: "32px" }, color: "#1b1f33", mb: 3, lineHeight: 1.3 }}>
                IT Infrastructure{" "}
                <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  You Can Trust
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, mb: 3, textAlign: "justify" }}>
                Your IT infrastructure is the backbone of your business. Our proactive managed services approach ensures continuous availability, peak performance, and robust security across all your systems. We handle everything from day-to-day operations to strategic technology planning.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, textAlign: "justify" }}>
                With predictable monthly costs, guaranteed SLAs, and a dedicated support team, you gain peace of mind and can redirect IT resources toward innovation and business growth. Our comprehensive monitoring and proactive maintenance prevent costly downtime and security breaches.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 4 — MIT Expertise: light gradient bg with numbered cards */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: "linear-gradient(135deg, #eef1ff 0%, #f3eeff 100%)" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              SERVICES
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Our Managed IT{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Services
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ color: "#4c5472", lineHeight: 1.8, maxWidth: 700, mx: "auto", fontSize: { xs: "16px", sm: "17px" } }}>
              Complete IT management from infrastructure to support with 24/7 monitoring and expert support.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {mitExpertise.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "16px", background: "#fff", borderTop: "1px solid rgba(91,124,250,0.1)", borderRight: "1px solid rgba(91,124,250,0.1)", borderBottom: "1px solid rgba(91,124,250,0.1)", borderLeft: "4px solid #5b7cfa", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { background: "#fafbff", transform: "translateY(-4px)", boxShadow: "0 16px 40px rgba(91,124,250,0.14)" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ color: "#fff", fontSize: "13px", fontWeight: 700 }}>{String(index + 1).padStart(2, "0")}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", fontSize: { xs: "17px", sm: "18px" } }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Section 5 — Why Choose Us: horizontal icon + text rows */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              WHY BRIOZON
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Why Choose Briozon for{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Managed IT Services
              </Box>
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} key={index} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, p: { xs: 3, md: 4 }, borderRadius: "16px", border: "1px solid rgba(91,124,250,0.1)", backgroundColor: "#fafbff", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(91,124,250,0.12)", borderColor: "rgba(91,124,250,0.3)", backgroundColor: "#fff" } }}>
                  <Box sx={{ width: 58, height: 58, borderRadius: "16px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.7rem", flexShrink: 0, boxShadow: "0 6px 16px rgba(91,124,250,0.25)" }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 0.75, fontSize: { xs: "18px", sm: "20px" } }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Section 6 — Service Offerings: top-border gradient cards */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              SOLUTIONS
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Our Managed IT{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Solutions
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ color: "#4c5472", lineHeight: 1.8, maxWidth: 700, mx: "auto", fontSize: { xs: "16px", sm: "17px" } }}>
              Comprehensive IT services spanning infrastructure, security, cloud, and 24/7 support.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {serviceOfferings.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "16px", background: "#fff", borderTop: "4px solid #5b7cfa", boxShadow: "0 2px 12px rgba(91,124,250,0.07)", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { transform: "translateY(-6px)", boxShadow: "0 16px 40px rgba(91,124,250,0.14)" } }}>
                  <Box sx={{ fontSize: "2rem", mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 1, fontSize: { xs: "18px", sm: "20px" } }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Ready to Optimize Your IT Operations?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              Let's discuss how managed IT services can enhance your infrastructure, reduce costs, and improve security. Schedule a consultation with our IT experts to develop a customized managed services plan.
            </Typography>
            <Button
              component="a"
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ManagedITServices;
